import {
	Alert,
	Badge,
	Box,
	Button,
	Col,
	Divider,
	Dropdown,
	Grid,
	Icons,
	Inline,
	Popover,
	RelativeTime,
	Spinner,
	Stack,
	Text,
	Time,
} from "@sembark-travel/ui-base"
import {
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useState } from "react"
import useSWR from "swr"
import { PERMISSIONS, useAuthUser, useCheckPermissions } from "../Auth"
import { IContact } from "../Contacts"
import { generatePath } from "../router-utils"
import { AddNewTrip } from "../Trips/NewItem"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { ITripSource } from "../TripSources"
import { TTripDestination } from "../TripDestinations"
import { MailMessageBody } from "./MailMessageBody"
import { ComposeEmail } from "./Compose/ComposeEmail"
import { getMailMessagePlainText } from "../shared/mailMessagePlainText"
import {
	getParsingBadge,
	PARSING_FEEDBACK_CATEGORIES,
	type TParsingFeedbackCategory,
	type TParsingSnapshot,
} from "./parsing"
import {
	Form,
	GetFieldValue,
	RadioInputField,
	SelectInputField,
	SubmissionError,
	TextAreaInputField,
	withServerErrors,
} from "@sembark-travel/ui-form"

export type TMailMessage = {
	id: number
	message_id: string
	direction?: "inbound" | "outbound" | null
	subject?: string | null
	created_by?: {
		id: number
		uid?: string
		name?: string | null
		email?: string | null
	} | null
	from_name?: string | null
	from_email?: string | null
	sent_at?: string | null
	received_at?: string | null
	preview?: string | null
	body_available?: boolean
	body_pending?: boolean
	html_body?: string | null
	text_body?: string | null
	latest_html_body?: string | null
	latest_text_body?: string | null
	quoted_html_body?: string | null
	quoted_text_body?: string | null
	// Every per-mailbox thread this canonical email belongs to (one per
	// mailbox it was delivered to), earliest-attached first.
	threads?: Array<{
		id: number
		mailbox_id: number
		subject?: string | null
		normalized_subject?: string | null
		latest_message_at?: string | null
	}>
	// Every mailbox this canonical email was delivered to (an email can land
	// in several of a tenant's mailboxes).
	mailboxes?: Array<{
		id: number
		name: string
		email: string
	}>
	links?: Array<{
		type: string
		id: number
		name: string
		// The linked entity's own source (e.g. the lead's trip_source) — used to
		// deep-link to the right source filter, not the synced mailbox's email.
		trip_source?: string | null
	}>
	participants?: Array<{
		id: number
		type: string
		name?: string | null
		email: string
	}>
	latest_parsing?: TParsingSnapshot | null
	send?: {
		status: "queued" | "sending" | "sent" | "failed"
		error?: string | null
		sent_at?: string | null
	} | null
	is_read?: boolean
	last_read_event?: {
		id: number
		action: "read" | "unread"
		source: "app" | "imap" | "system"
		user?: { id: number; name: string } | null
		changed_at?: string | null
	} | null
}

type TCreateTripPayload = {
	trip_source?: ITripSource | null
	trip_source_contact?: IContact | null
	reference_id?: string | null
	destinations?: Array<TTripDestination>
	start_date?: string | null
	no_of_days?: number
	no_of_nights?: number
	no_of_adults?: number
	children?: Array<{ age: number; count: number }>
	tourist?: {
		name?: string | null
		email?: string | null
		phone_numbers?: Array<string>
	}
	comments?: string | null
}

function SenderAvatar({
	name,
	email,
	dimmed,
}: {
	name?: string | null
	email?: string | null
	dimmed?: boolean
}) {
	const source = (name || email || "?").trim()
	const tokens = source.split(/[\s_.@-]+/).filter(Boolean)
	const initials = tokens.length
		? tokens
				.map((t) => t.match(/[A-Za-z0-9]/)?.[0] || "")
				.filter(Boolean)
				.slice(0, 2)
				.join("")
				.toUpperCase()
		: "?"
	return (
		<Box
			width="8"
			height="8"
			rounded="full"
			bgColor={dimmed ? "subtle" : "primary"}
			display="flex"
			alignItems="center"
			justifyContent="center"
			flexShrink="0"
			borderWidth="1"
			borderColor={dimmed ? "default" : "primary"}
		>
			<Text
				fontWeight="semibold"
				fontSize="xs"
				color={dimmed ? "muted" : "primary"}
			>
				{initials.slice(0, 2) || "?"}
			</Text>
		</Box>
	)
}

function MailMessageRowDetailsLoader({
	row,
	isExpanded,
	children,
}: {
	row: TMailMessage
	isExpanded: boolean
	children: (props: {
		message: TMailMessage
		isRevalidatingDetails: boolean
		hasDetails: boolean
	}) => React.ReactNode
}) {
	const xhr = useXHR()
	const { data, isValidating, mutate } = useSWR(
		isExpanded ? ["mail-message-details", row.id] : null,
		() =>
			xhr
				.get<{ data: TMailMessage }>(`/mail-messages/${row.id}`)
				.then((r) => r.data.data)
	)

	// if the parsing status changes
	useEffect(() => {
		mutate()
	}, [row.latest_parsing?.status, mutate])

	// A deferred (large) body lands in the background; the slim list poll flips
	// body_pending → refetch the detail so the full body renders.
	useEffect(() => {
		mutate()
	}, [row.body_pending, mutate])
	const hasDetails = Boolean(data)
	const merged: TMailMessage = isExpanded && data ? { ...row, ...data } : row
	const isRevalidatingDetails = isValidating && hasDetails
	return (
		<>
			{children({
				message: merged,
				isRevalidatingDetails,
				hasDetails,
			})}
		</>
	)
}

function MailBodyDisclosure({
	message,
	defaultOpen,
	view,
	onViewChange,
}: {
	message: TMailMessage
	defaultOpen?: boolean
	view?: "text" | "html"
	onViewChange?: (view: "text" | "html") => void
}) {
	const [open, setOpen] = useState(defaultOpen)
	return (
		<Stack gap="2">
			{!defaultOpen ? (
				<Inline>
					<Button size="sm" level="tertiary" onClick={() => setOpen(!open)}>
						<Icons.ChevronDown rotate={open ? "180" : undefined} />{" "}
						{open ? "Hide" : "View"} original email
					</Button>
				</Inline>
			) : null}
			{open ? (
				<MailMessageBody
					htmlBody={message.latest_html_body || message.html_body}
					textBody={message.latest_text_body || message.text_body}
					quotedHtmlBody={message.quoted_html_body}
					quotedTextBody={message.quoted_text_body}
					messageLabel="mail message"
					hideViewToggle
					view={view}
					onViewChange={onViewChange}
					bodyAvailable={message.body_available}
					bodyPending={message.body_pending}
					preview={message.preview}
				/>
			) : null}
		</Stack>
	)
}

function ExtractedField({
	label,
	value,
}: {
	label: string
	value: React.ReactNode
}) {
	return (
		<Box
			bgColor="default"
			rounded="md"
			paddingX="3"
			paddingY="2"
			borderWidth="1"
			borderColor="primary"
		>
			<Stack gap="0">
				<Text fontSize="xs" color="muted" textTransform="uppercase">
					{label}
				</Text>
				<Text fontWeight="semibold" fontSize="sm">
					{value}
				</Text>
			</Stack>
		</Box>
	)
}

function formatPerson({
	name,
	email,
}: {
	name?: string | null
	email?: string | null
}) {
	if (!email) {
		return name || "Unknown"
	}

	return name ? `${name} <${email}>` : email
}

function MailMessageDetailsPopover({ message }: { message: TMailMessage }) {
	const recipients = (message.participants || []).filter(
		(participant) => participant.type !== "from"
	)

	return (
		<Popover
			trigger="click"
			placement="bottom-start"
			content={
				<Box maxWidth="lg" overflow="auto">
					<Stack padding="4" gap="3">
						<Stack gap="1">
							<Text fontSize="sm" color="muted">
								From
							</Text>
							<Text fontSize="sm" whiteSpace="preserveAndWrap">
								{formatPerson({
									name: message.from_name,
									email: message.from_email,
								})}
							</Text>
						</Stack>
						{recipients.length ? (
							<Stack gap="2">
								<Text fontSize="sm" color="muted">
									Recipients
								</Text>
								<Stack gap="1">
									{recipients.map((participant) => (
										<Text
											key={participant.id}
											fontSize="sm"
											whiteSpace="preserveAndWrap"
										>
											{participant.type.toUpperCase()}:{" "}
											{formatPerson(participant)}
										</Text>
									))}
								</Stack>
							</Stack>
						) : null}
						{message.created_by ? (
							<Stack gap="1">
								<Text fontSize="sm" color="muted">
									Sent From System By
								</Text>
								<Text fontSize="sm">{formatPerson(message.created_by)}</Text>
							</Stack>
						) : null}
						{message.mailboxes?.length ? (
							<Stack gap="1">
								<Text fontSize="sm" color="muted">
									{message.mailboxes.length > 1 ? "Mailboxes" : "Mailbox"}
								</Text>
								{message.mailboxes.map((mailbox) => (
									<Text key={mailbox.id} fontSize="sm">
										{mailbox.name} &lt;{mailbox.email}&gt;
									</Text>
								))}
							</Stack>
						) : null}
					</Stack>
				</Box>
			}
		>
			<Button inline>
				<Text as="span" color="muted" fontSize="sm">
					{message.participants?.length
						? `${message.participants.length} participant${
								message.participants.length === 1 ? "" : "s"
							}`
						: "No recipients recorded"}
				</Text>{" "}
				<Icons.ChevronDown rotate="270" />
			</Button>
		</Popover>
	)
}

function ParsingFeedback({
	message,
	refresh,
	isExpanded,
}: {
	message: TMailMessage
	refresh: () => unknown
	isExpanded: boolean
}) {
	const xhr = useXHR()
	const [initialFeedback, setInitialFeedback] = useState<0 | 1 | undefined>(
		undefined
	)
	const parsing = message.latest_parsing
	if (!parsing || parsing.status !== "completed") {
		return null
	}
	const current = parsing.feedback_status

	function closeDialog() {
		setInitialFeedback(undefined)
	}

	if (
		!isExpanded &&
		(parsing.feedback_status === null || parsing.feedback_status === undefined)
	) {
		return null
	}

	return (
		<object
			onClick={(e) => {
				// block the click propogation to avoid isExpanded toggle by parent
				e.stopPropagation()
			}}
		>
			<Inline gap="1" alignItems="center">
				{parsing.feedback_status === 0 ? null : (
					<Button
						inline={!isExpanded}
						size="sm"
						title="Good parse"
						status="success"
						level="tertiary"
						onClick={(e) => {
							e.stopPropagation()
							setInitialFeedback(1)
						}}
					>
						<Text as="span" color={current === 1 ? "success" : "muted"}>
							{current === 1 ? (
								<Icons.HandThumbUpSolid />
							) : (
								<Icons.HandThumbUp />
							)}
						</Text>
					</Button>
				)}
				{parsing.feedback_status === 1 ? null : (
					<Button
						inline={!isExpanded}
						size="sm"
						status="danger"
						title="Bad parse"
						level="tertiary"
						onClick={(e) => {
							e.stopPropagation()
							setInitialFeedback(0)
						}}
					>
						<Text as="span" color={current === 0 ? "danger" : "muted"}>
							{current === 0 ? (
								<Icons.HandThumbUpSolid rotate="180" />
							) : (
								<Icons.HandThumbUp rotate="180" />
							)}
						</Text>
					</Button>
				)}
				<Dialog
					open={initialFeedback !== undefined}
					onClose={closeDialog}
					title="Share your feedback for AI"
				>
					<Dialog.Body>
						<Form<{
							status: "positive" | "negative"
							category: TParsingFeedbackCategory | undefined
							remarks: string
						}>
							initialValues={{
								status: initialFeedback === 1 ? "positive" : "negative",
								category: parsing.feedback_category as TParsingFeedbackCategory,
								remarks: parsing.feedback_remarks || "",
							}}
							onSubmit={withServerErrors(async (values) => {
								if (
									values.status === "negative" &&
									values.category === "others" &&
									!values.remarks?.trim()
								) {
									throw Error("Please add remarks where AI should be improved.")
								}
								const category =
									values.category &&
									PARSING_FEEDBACK_CATEGORIES.filter((cat) =>
										values.status === "positive"
											? cat.type === 1
											: cat.type === 0
									)
										.map((cat) => cat.value)
										.indexOf(values.category) !== -1
										? values.category
										: undefined
								if (!category && values.status === "negative") {
									throw Error("Please select a category in which AI failed.")
								}
								await xhr.post(`/mail-messages/${message.id}/feedback`, {
									status: values.status === "positive" ? 1 : 0,
									category,
									remarks: values.remarks || undefined,
								})
								closeDialog()
								showSnackbar("Thanks for the feedback.")
								refresh()
							})}
							subscription={{ submitting: true }}
						>
							{({ submitting, handleSubmit }) => (
								<form noValidate onSubmit={handleSubmit}>
									<Stack gap="4">
										<Text color="muted">
											Please share your positive/negative feedback for this AI
											parsing. Let's build a better version of it.
										</Text>
										<GetFieldValue<"positive" | "negative"> name="status">
											{({ value: status }) => {
												const feedbackCategoryOptionsForStatus =
													PARSING_FEEDBACK_CATEGORIES.filter((cat) =>
														status === "positive"
															? cat.type === 1
															: cat.type === 0
													)
												return (
													<>
														<Inline
															borderWidth="1"
															rounded="md"
															overflow="hidden"
															collapseBelow="sm"
														>
															<Box
																as="label"
																flex="1"
																padding="4"
																position="relative"
																bgColor={
																	status === "positive" ? "success" : undefined
																}
															>
																<Box
																	position="absolute"
																	width="0"
																	height="0"
																	overflow="hidden"
																>
																	<RadioInputField
																		name="status"
																		value="positive"
																	/>
																</Box>
																<Inline gap="2">
																	<Box>
																		{status === "positive" ? (
																			<Icons.HandThumbUpSolid
																				color="success"
																				size="6"
																			/>
																		) : (
																			<Icons.HandThumbUp size="6" />
																		)}
																	</Box>
																	<Stack minWidth="0" gap="1" color="success">
																		<Text fontWeight="semibold">
																			Good Parsing
																		</Text>
																		<Text fontSize="sm" fontWeight="normal">
																			The AI parsing did a good job in the
																			classification as well as the fields
																			extraction.
																		</Text>
																	</Stack>
																</Inline>
															</Box>
															<Box
																as="label"
																flex="1"
																padding="4"
																position="relative"
																bgColor={
																	status === "negative" ? "danger" : undefined
																}
															>
																<Box
																	position="absolute"
																	width="0"
																	height="0"
																	overflow="hidden"
																>
																	<RadioInputField
																		name="status"
																		value="negative"
																	/>
																</Box>
																<Inline gap="2">
																	<Box>
																		{status === "negative" ? (
																			<Icons.HandThumbUpSolid
																				rotate="180"
																				color="danger"
																				size="6"
																			/>
																		) : (
																			<Icons.HandThumbUp
																				rotate="180"
																				size="6"
																			/>
																		)}
																	</Box>
																	<Stack minWidth="0" gap="1" color="danger">
																		<Text fontWeight="semibold">
																			Bad Parsing
																		</Text>
																		<Text fontSize="sm" fontWeight="normal">
																			The AI parsing needs improvements in
																			either classification or in fields
																			extraction.
																		</Text>
																	</Stack>
																</Inline>
															</Box>
														</Inline>
														{feedbackCategoryOptionsForStatus.length ? (
															<SelectInputField
																label="What went Wrong?"
																name="category"
																required
															>
																<option value="">---</option>
																{feedbackCategoryOptionsForStatus.map((c) => (
																	<option key={c.value} value={c.value}>
																		{c.label}
																	</option>
																))}
															</SelectInputField>
														) : null}
														<GetFieldValue<TParsingFeedbackCategory> name="category">
															{({ value }) => (
																<TextAreaInputField
																	label="Remarks"
																	name="remarks"
																	rows={3}
																	secondaryLabel={
																		status === "positive" || value !== "others"
																			? "optional"
																			: undefined
																	}
																	placeholder={
																		status === "positive"
																			? "Any feedback or suggestions you want to give"
																			: "What was wrong with the parse?"
																	}
																/>
															)}
														</GetFieldValue>
													</>
												)
											}}
										</GetFieldValue>
										<Divider marginY="0" />
										<SubmissionError />
										<Inline gap="4">
											<Button type="submit" disabled={submitting}>
												{submitting ? "Saving..." : "Submit"}
											</Button>
											<Button
												level="tertiary"
												onClick={closeDialog}
												disabled={submitting}
											>
												Cancel
											</Button>
										</Inline>
									</Stack>
								</form>
							)}
						</Form>
					</Dialog.Body>
				</Dialog>
			</Inline>
		</object>
	)
}

function CreateTripFromMailMessageDialog({
	message,
	onSuccess,
	children,
}: {
	message: TMailMessage
	onSuccess: (tripId: number) => void
	children: (props: { onCreate: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	const xhr = useXHR()
	const subject =
		message.subject || message.threads?.[0]?.subject || "(No Subject)"
	const { data } = useSWR<TCreateTripPayload>(
		isOpen ? `/mail-message-trip-payload/${message.id}` : null,
		() =>
			xhr
				.get(`/mail-messages/${message.id}/trip/create`)
				.then((resp) => resp.data.data)
	)

	return (
		<>
			{children({ onCreate: open })}
			<Dialog open={isOpen} onClose={close} title="Create Trip from Mail" xl>
				<Dialog.Body>
					<Stack gap="4">
						<Stack gap="2">
							<Text fontWeight="semibold">{subject}</Text>
							<Inline gap="2" alignItems="center" flexWrap="wrap">
								<Text color="muted" fontSize="sm">
									{formatPerson({
										name: message.from_name,
										email: message.from_email,
									})}
								</Text>
								{message.received_at || message.sent_at ? (
									<Text color="muted" fontSize="sm">
										<Time
											timeFormat
											dateFormat
											timestamp={message.received_at || message.sent_at || ""}
										/>
									</Text>
								) : null}
							</Inline>
							<Box>
								<MailMessageBody
									htmlBody={message.latest_html_body || message.html_body}
									textBody={message.latest_text_body || message.text_body}
									quotedHtmlBody={message.quoted_html_body}
									quotedTextBody={message.quoted_text_body}
									messageLabel="mail message"
									bodyAvailable={message.body_available}
									bodyPending={message.body_pending}
									preview={message.preview}
								/>
							</Box>
						</Stack>
						<Divider />
						{!data ? (
							<Spinner padding="4" alignCenter />
						) : (
							<AddNewTrip
								initialValues={{
									reference_id: data.reference_id || "",
									start_date: undefined,
									no_of_days: data.no_of_days,
									no_of_nights: data.no_of_nights,
									destinations: data.destinations || [],
									no_of_adults: data.no_of_adults,
									children: data.children || [],
									trip_source: data.trip_source || undefined,
									trip_source_contact: data.trip_source_contact || undefined,
									comments: data.comments || "",
									tourist: {
										name: data.tourist?.name || "",
										email: data.tourist?.email || "",
										phone_numbers: [],
									},
								}}
								onSuccess={async (trip) => {
									await xhr.post(`/mail-messages/${message.id}/trip`, {
										trip_id: trip.id,
									})
									showSnackbar(
										`Trip #${trip.id} created and linked to the mail.`
									)
									close()
									onSuccess(trip.id)
								}}
								onCancel={close}
							/>
						)}
					</Stack>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function MailMessagesList({
	modelType,
	modelId,
	title = "Mail Messages",
	emptyStateMessage = "No inbound messages are available for the mailboxes you can access.",
	onChange,
	syncQueryToUrl,
}: {
	modelType?: "trips" | "trip_plan_requests" | "hotel_bookings"
	modelId?: number
	title?: string | null
	emptyStateMessage?: string
	onChange?: () => unknown
	// Persist the search (q/page) to the URL. Defaults ON for the standalone
	// listing and OFF when embedded under a modelId/Type — because an embedded
	// list is usually a sub-part of a larger search (or a per-row modal) where
	// syncing would fight over the URL. A dedicated page that embeds this (e.g.
	// a trip's Emails route) can opt back in to make messages deep-linkable.
	syncQueryToUrl?: boolean
} = {}) {
	const syncQuery = syncQueryToUrl ?? !(modelId && modelType)
	const [query, setQuery] = useLocationQuery()
	const [params, setParams] = useSearch<TSearchParams>(
		syncQuery
			? {
					...query,
				}
			: undefined
	)

	useEffect(() => {
		if (syncQuery) {
			setQuery(params)
		}
	}, [params, setQuery, syncQuery])
	const { hasPermission } = useCheckPermissions()
	const canCreateTrip = hasPermission(PERMISSIONS.CREATE_QUERIES)
	// Multiple rows can be open at once (avoids scroll-hunting a long thread).
	const { user } = useAuthUser()
	// New emails can be about a Trip or a lead, not a hotel booking.
	const composeModelType =
		modelType === "trips" || modelType === "trip_plan_requests"
			? modelType
			: undefined
	const [expandedIds, setExpandedIds] = useState<ReadonlySet<number>>(
		() => new Set()
	)
	const toggleExpanded = (id: number, open?: boolean) =>
		setExpandedIds((current) => {
			const next = new Set(current)
			const shouldOpen = open ?? !next.has(id)
			shouldOpen ? next.add(id) : next.delete(id)
			return next
		})
	// Rich vs plain view, per message — toggled from the row's actions dropdown
	// (a secondary action), so the body itself hides its own buttons.
	const [bodyViews, setBodyViews] = useState<Record<number, "text" | "html">>(
		{}
	)
	const bodyViewFor = (id: number): "text" | "html" => bodyViews[id] ?? "html"
	const setBodyViewFor = (id: number, view: "text" | "html") =>
		setBodyViews((current) => ({ ...current, [id]: view }))
	const xhr = useXHR()
	const reparse = (id: number, refresh: () => unknown) =>
		xhr
			.post(`/mail-messages/${id}/reparse`)
			.then(() => {
				showSnackbar("Re-parsing queued.")
				refresh()
			})
			.catch((error: Error) => {
				showSnackbar(error.message || "Could not start re-parsing.")
			})

	const markRead = (id: number, refresh: () => unknown) =>
		xhr
			.post(`/mail-messages/${id}/mark-read`)
			.then(() => refresh())
			.then(() => onChange?.())
			.catch(() => undefined)
	const markUnread = (id: number, refresh: () => unknown) =>
		xhr
			.post(`/mail-messages/${id}/mark-unread`)
			.then(() => refresh())
			.then(() => onChange?.())
			.catch(() => undefined)
	const scopeKey =
		modelType && modelId
			? `mail-messages/${modelType}/${modelId}`
			: "accessible-mail-messages"

	return (
		<Search
			title={title}
			initialParams={params}
			onSearch={(nextParams) => setParams({ ...nextParams, page: 1 })}
			placeholder="Search subject"
			actions={
				composeModelType && modelId && user?.reply_mailboxes_count ? (
					<ComposeEmail
						title="Send Email"
						modelType={composeModelType}
						modelId={modelId}
						onSuccess={onChange}
					>
						{({ onCompose }) => (
							<Button onClick={onCompose}>
								<Icons.Mail /> Send Email
							</Button>
						)}
					</ComposeEmail>
				) : !modelType &&
				  !modelId &&
				  hasPermission(PERMISSIONS.MANAGE_ACCOUNTING) ? (
					<ButtonLink to={generatePath("/credit-consumption/mail-parsing")}>
						<Icons.SparklesSolid /> Parsing Usage
					</ButtonLink>
				) : undefined
			}
		>
			<ListView<TMailMessage>
				pageKey={`${scopeKey}`}
				params={params}
				fetch={(api, listParams) =>
					api
						.get("/mail-messages", {
							params: {
								...listParams,
								...(modelType && modelId
									? { model_type: modelType, model_id: modelId }
									: {}),
							},
						})
						.then((resp) => resp.data)
				}
				onPageChange={(page) => setParams((current) => ({ ...current, page }))}
				emptyState={() => <Alert status="info">{emptyStateMessage}</Alert>}
				swrConfig={{ refreshInterval: 5000 }}
			>
				{({ items, refresh }) => (
					<Stack
						gap="0"
						borderWidth="1"
						rounded="md"
						bgColor="default"
						overflow="hidden"
					>
						{items.map((row, index) => {
							const isRowExpanded = expandedIds.has(row.id)
							return (
								<MailMessageRowDetailsLoader
									key={row.id}
									row={row}
									isExpanded={isRowExpanded}
								>
									{({
										message,
										isRevalidatingDetails: isLoadingDetails,
										hasDetails,
									}) => {
										const isExpanded = isRowExpanded
										const linkedTrip = message.links?.find(
											(link) => link.type === "trips"
										)
										const linkedTripPlanRequest = message.links?.find(
											(link) => link.type === "trip_plan_requests"
										)
										const primaryLink =
											linkedTrip && modelType !== "trips"
												? { kind: "trip" as const, link: linkedTrip }
												: linkedTripPlanRequest && !modelType
													? {
															kind: "tpr" as const,
															link: linkedTripPlanRequest,
														}
													: null
										const parsingBadge = getParsingBadge(message.latest_parsing)
										const extraction = message.latest_parsing?.extraction
										const hasExtractionFields = extraction
											? Boolean(
													extraction.guest_name ||
													extraction.destinations?.length ||
													extraction.start_date ||
													extraction.no_of_nights ||
													extraction.no_of_adults ||
													extraction.contact_email ||
													extraction.contact_person ||
													extraction.guest_phone_numbers ||
													extraction.accomodation_category ||
													extraction.meal_preferences ||
													extraction.budget
												)
											: false
										const pax = extraction
											? [
													extraction.no_of_adults
														? `${extraction.no_of_adults} adult${
																extraction.no_of_adults === 1 ? "" : "s"
															}`
														: null,
													extraction.no_of_children
														? `${extraction.no_of_children} kid${
																extraction.no_of_children === 1 ? "" : "s"
															}`
														: null,
												]
													.filter(Boolean)
													.join(" + ")
											: ""
										const leadFilterHref = `${generatePath(
											"/trip-plan-requests"
										)}?q=${encodeURIComponent(
											String("#" + linkedTripPlanRequest?.id)
										)}&source=${encodeURIComponent(
											linkedTripPlanRequest?.trip_source ||
												message.mailboxes?.[0]?.email ||
												""
										)}`
										const subject =
											message.subject ||
											message.threads?.[0]?.subject ||
											"(No Subject)"
										// A deferred (large) message has no body/preview yet — show
										// a loading hint instead of "No message content available".
										const preview = message.body_pending
											? "Large message - click to load and view the content"
											: getMailMessagePlainText({
													textBody:
														message.latest_text_body || message.text_body,
													htmlBody:
														message.latest_html_body || message.html_body,
												})

										const isUnread = !message.is_read
										const classification =
											message.latest_parsing?.classification
										const isLead =
											message.latest_parsing?.status === "completed" &&
											classification === "quotation_request"
										const isDeprioritized =
											(modelId && modelType) || primaryLink
												? false
												: message.latest_parsing?.status === "skipped" ||
													(message.latest_parsing?.status === "completed" &&
														(classification === "not_relevant" ||
															classification === "no_enquiry_found" ||
															classification === "spam"))
										const rowBgColor: "inset" | "default" =
											isDeprioritized && !isExpanded ? "inset" : "default"
										return (
											<Box
												key={message.id}
												borderBottomWidth={
													index === items.length - 1 ? "0" : "1"
												}
												bgColor={rowBgColor}
												position="relative"
											>
												<Box
													position="absolute"
													left="0"
													height="full"
													borderLeftColor={
														isUnread
															? "accent_emphasis"
															: isExpanded
																? isDeprioritized
																	? "emphasis"
																	: "primary_emphasis"
																: "transparent"
													}
													borderLeftWidth={"2"}
												></Box>
												<Stack>
													<Inline
														width="full"
														textAlign="left"
														paddingX="4"
														paddingY="3"
														gap={{ xs: "2", sm: "4" }}
														alignItems={{ xs: "start", sm: "center" }}
														collapseBelow="sm"
													>
														<Inline
															as="a"
															href={`#m${message.id}`}
															gap="4"
															alignItems="center"
															flex="1"
															minWidth="0"
															width="full"
															onClick={(e) => {
																e.preventDefault()
																const willExpand = !isExpanded
																toggleExpanded(message.id, willExpand)
																if (willExpand && isUnread) {
																	markRead(message.id, refresh)
																}
															}}
														>
															<Box display={{ xs: "none", md: "block" }}>
																{modelType ? (
																	<Inline
																		bgColor="primary"
																		paddingY="1"
																		borderColor="primary"
																		paddingX="2"
																		borderWidth="1"
																		rounded="md"
																		alignItems="center"
																		justifyContent="center"
																	>
																		{message.direction === "inbound" ? (
																			<Icons.Mail
																				color="primary"
																				title="Received"
																			/>
																		) : (
																			<Icons.Reply
																				color="primary"
																				title="Sent"
																			/>
																		)}
																	</Inline>
																) : (
																	<SenderAvatar
																		name={message.from_name}
																		email={message.from_email}
																		dimmed={isDeprioritized}
																	/>
																)}
															</Box>
															<Inline
																gap={{ xs: "px", md: "4" }}
																flex="1"
																minWidth="0"
																collapseBelow="md"
																alignItems={{ md: "center" }}
															>
																<Box minWidth="0">
																	<Text
																		fontWeight={
																			isUnread ? "semibold" : "normal"
																		}
																		color={
																			isDeprioritized ? "muted" : "default"
																		}
																		textOverflow="truncate"
																		whiteSpace="nowrap"
																	>
																		{message.from_name ||
																			message.from_email ||
																			"Unknown Sender"}
																	</Text>
																</Box>
																{isExpanded ? (
																	<Box flex="1"></Box>
																) : (
																	<Box flex="1" minWidth="0">
																		<Box
																			display={{ xs: "block", sm: "none" }}
																			fontSize="sm"
																			color={
																				isDeprioritized ? "muted" : "default"
																			}
																		>
																			{subject}
																		</Box>
																		<Box display={{ xs: "none", sm: "block" }}>
																			<Text
																				fontSize="sm"
																				textOverflow="truncate"
																				whiteSpace="nowrap"
																			>
																				<Text
																					as="span"
																					fontWeight={
																						isUnread ? "semibold" : "normal"
																					}
																					color={
																						isDeprioritized
																							? "muted"
																							: "default"
																					}
																				>
																					{subject}
																				</Text>
																				<Text as="span" color="muted">
																					{" - "}
																					{preview}
																				</Text>
																			</Text>
																		</Box>
																	</Box>
																)}
															</Inline>
														</Inline>
														<Inline gap="2" alignItems="center" flexWrap="wrap">
															{message.direction === "outbound" ? (
																message.send?.status === "queued" ||
																message.send?.status === "sending" ? (
																	<Badge status="warning" fullRounded>
																		<Icons.Reply size="3" /> Sending…
																	</Badge>
																) : message.send?.status === "failed" ? (
																	<Badge status="danger" fullRounded>
																		<Icons.Attention size="3" /> Failed to send
																	</Badge>
																) : (
																	<Badge fullRounded outlined>
																		<Icons.Reply size="3" /> Sent
																		{message.created_by?.name
																			? ` by ${message.created_by.name}`
																			: null}
																	</Badge>
																)
															) : null}
															{isLead ? (
																<Badge status="primary" fullRounded>
																	<Icons.SparklesSolid size="3" /> AI Lead
																</Badge>
															) : null}
															{!isLead &&
															message.latest_parsing?.status === "completed" &&
															classification === "support" ? (
																<Badge status="accent" fullRounded>
																	<Icons.SparklesSolid size="3" /> Support
																</Badge>
															) : null}
															{!isLead &&
															message.latest_parsing?.status === "completed" &&
															classification === "not_relevant" ? (
																<Badge fullRounded outlined>
																	Not Relevant
																</Badge>
															) : null}
															{!isLead &&
															message.latest_parsing?.status === "completed" &&
															classification === "no_enquiry_found" ? (
																<Badge fullRounded outlined>
																	<Icons.SparklesSolid size="3" /> No Enquiry
																	Found
																</Badge>
															) : null}
															{!isLead &&
															message.latest_parsing?.status === "completed" &&
															classification === "spam" ? (
																<Badge fullRounded outlined>
																	Spam
																</Badge>
															) : null}
															{!isLead &&
															parsingBadge &&
															message.latest_parsing?.status !== "completed" &&
															!modelId ? (
																<Badge
																	status={
																		parsingBadge.color === "success"
																			? "success"
																			: parsingBadge.color === "danger"
																				? "danger"
																				: parsingBadge.color === "warning"
																					? "warning"
																					: undefined
																	}
																	fullRounded
																	title={parsingBadge.detail}
																>
																	{parsingBadge.label}
																</Badge>
															) : null}
															{primaryLink?.kind === "trip" ? (
																<Link
																	to={generatePath("/trips/:tripId", {
																		tripId: String(primaryLink.link.id),
																	})}
																>
																	<Badge status="success" fullRounded>
																		Trip #{primaryLink.link.id}
																	</Badge>
																</Link>
															) : primaryLink?.kind === "tpr" ? (
																<Link to={leadFilterHref}>
																	<Badge status="success" fullRounded>
																		TPR #{primaryLink.link.id}
																	</Badge>
																</Link>
															) : null}
															<ParsingFeedback
																message={message}
																refresh={refresh}
																isExpanded={isExpanded}
															/>
															{(message.received_at || message.sent_at) &&
															!isExpanded ? (
																<Text
																	fontSize="sm"
																	color={isDeprioritized ? "muted" : "default"}
																>
																	<RelativeTime
																		timestamp={
																			message.received_at ||
																			message.sent_at ||
																			""
																		}
																	/>
																</Text>
															) : null}
														</Inline>
													</Inline>
													{isExpanded ? (
														<Stack gap="4" paddingX="4" paddingBottom="4">
															<Inline
																justifyContent="between"
																alignItems="start"
																flexWrap="wrap"
																gap="3"
															>
																<Stack gap="1">
																	<Text fontWeight="semibold">
																		{subject}
																		{isLoadingDetails && hasDetails ? (
																			<Text
																				as="span"
																				color="muted"
																				fontSize="sm"
																				fontWeight="normal"
																			>
																				{" "}
																				<Icons.Refresh spin size="3" />
																			</Text>
																		) : null}
																	</Text>
																	<Inline gap="2" alignItems="center">
																		{message.received_at || message.sent_at ? (
																			<Text color="muted" fontSize="sm">
																				<Time
																					timeFormat
																					dateFormat
																					timestamp={
																						message.received_at ||
																						message.sent_at ||
																						""
																					}
																				/>
																			</Text>
																		) : null}
																		<Box fontSize="sm">•</Box>
																		<MailMessageDetailsPopover
																			message={message}
																		/>
																	</Inline>
																</Stack>
																<Inline gap="2" alignItems="end">
																	{!linkedTrip &&
																	!linkedTripPlanRequest &&
																	canCreateTrip &&
																	!modelId ? (
																		<CreateTripFromMailMessageDialog
																			message={message}
																			onSuccess={() => {
																				toggleExpanded(message.id, false)
																				refresh()
																			}}
																		>
																			{({ onCreate }) => (
																				<Button size="sm" onClick={onCreate}>
																					Create Trip
																				</Button>
																			)}
																		</CreateTripFromMailMessageDialog>
																	) : null}
																	<ComposeEmail
																		title="Reply"
																		messageId={message.id}
																		onSuccess={() => {
																			refresh()
																			onChange?.()
																		}}
																	>
																		{({ onCompose }) => (
																			<Button size="sm" onClick={onCompose}>
																				<Icons.Reply /> Reply
																			</Button>
																		)}
																	</ComposeEmail>
																	<Dropdown alignRight>
																		<Dropdown.ToggleButton size="sm">
																			<Icons.DotsVertical />
																		</Dropdown.ToggleButton>
																		<Dropdown.Menu>
																			<Dropdown.MenuItem
																				onClick={(e) => {
																					e.preventDefault()
																					message.is_read
																						? markUnread(message.id, refresh)
																						: markRead(message.id, refresh)
																				}}
																			>
																				{message.is_read ? (
																					<Text as="span" color="accent">
																						<Icons.EyeSlash /> Mark as Unread
																					</Text>
																				) : (
																					<>
																						<Icons.Eye /> Mark as Read
																					</>
																				)}
																			</Dropdown.MenuItem>
																			{isRowExpanded &&
																			(
																				message.latest_html_body ||
																				message.html_body
																			)?.trim() ? (
																				<>
																					<Dropdown.MenuItemDivider />
																					<Dropdown.MenuItem
																						onClick={(e) => {
																							e.preventDefault()
																							setBodyViewFor(
																								message.id,
																								bodyViewFor(message.id) ===
																									"html"
																									? "text"
																									: "html"
																							)
																						}}
																					>
																						{bodyViewFor(message.id) ===
																						"html" ? (
																							<>
																								<Icons.CodeXml /> View as Plain
																								Text
																							</>
																						) : (
																							<>
																								<Icons.Eye /> View as Rich Text
																							</>
																						)}
																					</Dropdown.MenuItem>
																				</>
																			) : null}
																			{message.latest_parsing?.status ===
																			"failed" ? (
																				<Dropdown.MenuItem
																					onClick={(e) => {
																						e.preventDefault()
																						reparse(message.id, refresh)
																					}}
																				>
																					<Text as="span" color="warning">
																						<Icons.Refresh /> Reparse
																					</Text>
																				</Dropdown.MenuItem>
																			) : null}
																		</Dropdown.Menu>
																	</Dropdown>
																</Inline>
															</Inline>
															{modelId ? null : primaryLink?.kind === "trip" ? (
																<Box
																	borderWidth="1"
																	rounded="md"
																	bgColor="subtle"
																	padding="3"
																>
																	<Inline
																		justifyContent="between"
																		alignItems="center"
																		flexWrap="wrap"
																		gap="3"
																	>
																		<Inline gap="2" alignItems="center">
																			<Icons.Annotation />
																			<Text fontWeight="semibold">
																				{primaryLink.link.name}
																			</Text>
																		</Inline>
																		<ButtonLink
																			size="sm"
																			level="tertiary"
																			to={generatePath("/trips/:tripId", {
																				tripId: String(primaryLink.link.id),
																			})}
																		>
																			Open Trip{" "}
																			<Icons.ChevronDown rotate="270" />
																		</ButtonLink>
																	</Inline>
																</Box>
															) : primaryLink?.kind === "tpr" ? (
																<Box
																	borderWidth="1"
																	borderColor="primary"
																	rounded="md"
																	bgColor="primary"
																	padding="4"
																>
																	<Stack gap="3">
																		<Inline
																			justifyContent="between"
																			alignItems="center"
																			flexWrap="wrap"
																			gap="3"
																		>
																			<Inline gap="2" alignItems="center">
																				<Text color="primary">
																					<Icons.SparklesSolid />
																				</Text>
																				<Text
																					fontWeight="semibold"
																					color="primary"
																				>
																					{extraction
																						? "AI extracted these details"
																						: primaryLink.link.name}
																				</Text>
																			</Inline>
																			<ButtonLink
																				size="sm"
																				to={leadFilterHref}
																				level="primary"
																			>
																				View Lead #{primaryLink.link.id}{" "}
																				<Icons.ChevronDown rotate="270" />
																			</ButtonLink>
																		</Inline>
																		{extraction && hasExtractionFields ? (
																			<Grid gap="2">
																				{extraction.guest_name ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Guest"
																							value={[
																								extraction.guest_salutation,
																								extraction.guest_name,
																							]
																								.filter(Boolean)
																								.join(" ")}
																						/>
																					</Col>
																				) : null}
																				{extraction.destinations?.length ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Destinations"
																							value={extraction.destinations.join(
																								", "
																							)}
																						/>
																					</Col>
																				) : null}
																				{extraction.start_date ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Start Date"
																							value={
																								<Time
																									dateFormat
																									timestamp={
																										extraction.start_date
																									}
																								/>
																							}
																						/>
																					</Col>
																				) : null}
																				{extraction.no_of_nights ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Nights"
																							value={`${extraction.no_of_nights}N`}
																						/>
																					</Col>
																				) : null}
																				{pax ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Pax"
																							value={pax}
																						/>
																					</Col>
																				) : null}
																				{extraction.contact_email ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Source"
																							value={extraction.contact_email}
																						/>
																					</Col>
																				) : null}
																				{extraction.contact_person ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Contact Person"
																							value={[
																								extraction.contact_person,
																								extraction.contact_phone,
																							]
																								.filter(Boolean)
																								.join(" · ")}
																						/>
																					</Col>
																				) : null}
																				{extraction.guest_phone_numbers ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Guest Phone"
																							value={
																								Array.isArray(
																									extraction.guest_phone_numbers
																								)
																									? extraction.guest_phone_numbers.join(
																											", "
																										)
																									: extraction.guest_phone_numbers
																							}
																						/>
																					</Col>
																				) : null}
																				{extraction.accomodation_category ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Accommodation"
																							value={
																								extraction.accomodation_category
																							}
																						/>
																					</Col>
																				) : null}
																				{extraction.meal_preferences ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Meal Preferences"
																							value={
																								extraction.meal_preferences
																							}
																						/>
																					</Col>
																				) : null}
																				{extraction.budget ? (
																					<Col xs={12} sm={6} md={4}>
																						<ExtractedField
																							label="Budget"
																							value={[
																								extraction.budget_currency,
																								extraction.budget,
																							]
																								.filter(Boolean)
																								.join(" ")}
																						/>
																					</Col>
																				) : null}
																			</Grid>
																		) : null}
																		{extraction?.comments ? (
																			<Box
																				bgColor="default"
																				borderLeftWidth="4"
																				borderLeftColor="primary_emphasis"
																				rounded="md"
																				padding="3"
																			>
																				<Stack gap="1">
																					<Text
																						fontSize="xs"
																						color="muted"
																						textTransform="uppercase"
																					>
																						Detected intent
																					</Text>
																					<Text
																						whiteSpace="preserveAndWrap"
																						fontSize="sm"
																					>
																						{extraction.comments}
																					</Text>
																				</Stack>
																			</Box>
																		) : null}
																	</Stack>
																</Box>
															) : null}
															{!hasDetails ? (
																<Spinner alignCenter padding="4" />
															) : (
																<MailBodyDisclosure
																	message={message}
																	defaultOpen={
																		!isLead || primaryLink?.kind === "trip"
																	}
																	view={bodyViewFor(message.id)}
																	onViewChange={(v) =>
																		setBodyViewFor(message.id, v)
																	}
																/>
															)}
														</Stack>
													) : null}
												</Stack>
											</Box>
										)
									}}
								</MailMessageRowDetailsLoader>
							)
						})}
					</Stack>
				)}
			</ListView>
		</Search>
	)
}
